import { useState } from 'react'

import { PasswordInput } from '../lib/PasswordInput'
import type { FormEvent } from 'react'

import { ApiError } from '../lib/api'
import { useSession } from '../lib/SessionContext'
import { login } from '../lib/session'
import type { SessionKind } from '../lib/session'

type Props = {
  kind: SessionKind
  usernameLabel: string
  usernamePlaceholder: string
}

/**
 * ฟอร์มล็อกอินที่ใช้ร่วมกันสองทาง (admin / user) — ต่างกันแค่ข้อความกับ endpoint
 * รวมไว้เพราะการดัก error (429 ถูกล็อก, รหัสผิด) ต้องเหมือนกันทั้งสองทาง
 * ถ้าก๊อปไปสองที่ แก้ที่เดียวแล้วอีกทางจะพฤติกรรมไม่ตรงกัน
 *
 * หน้าตา/แบรนด์ของแต่ละทางอยู่ในไฟล์หน้านั้น ๆ ไม่ใช่ที่นี่
 */
export default function LoginForm({ kind, usernameLabel, usernamePlaceholder }: Props) {
  const { signIn, notice, clearNotice } = useSession()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<{ message: string; status: number } | null>(null)

  const canSubmit = !submitting && username.trim().length > 0 && password.length > 0

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)
    // ข้อความค้างจากรอบก่อน (เช่น "เซสชันหมดอายุ") ต้องหาย ไม่งั้นซ้อนกับ error ใหม่
    clearNotice()

    try {
      // ล็อกอินสำเร็จแล้วไม่ต้อง navigate เอง — guard ในหน้า login เห็นว่า signed-in
      // แล้วพาไปปลายทางเองที่เดียว (ถ้า navigate ที่นี่ด้วยจะแย่งกันสองที่)
      signIn(await login(kind, username.trim(), password))
    } catch (err) {
      // ไม่เก็บรหัสที่พิมพ์ผิดค้างในฟอร์ม
      setPassword('')
      setError({
        message: err instanceof Error ? err.message : String(err),
        status: err instanceof ApiError ? err.status : 0,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <label>
        {usernameLabel}
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={usernamePlaceholder}
          autoComplete="username"
          autoFocus
          required
        />
      </label>

      <label>
        รหัสผ่าน
        <PasswordInput
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />
      </label>

      {notice && <p className="warn">{notice}</p>}

      {/* 429 = กรอกผิดหลายครั้งจนถูกล็อก — backend บอกจำนวนนาทีมาในข้อความแล้ว
          ใช้โทนเตือน ไม่ใช่โทนผิดพลาด เพราะรอแล้วก็เข้าได้ */}
      {error && <p className={error.status === 429 ? 'warn' : 'error'}>{error.message}</p>}

      <button type="submit" disabled={!canSubmit}>
        {submitting ? 'กำลังตรวจสอบ…' : 'เข้าสู่ระบบ'}
      </button>
    </form>
  )
}
